import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { 
  LayoutDashboard, Truck, ClipboardList, Plus, LogOut, 
  Menu, X, ChevronRight, Key, Eye, EyeOff, Home, BarChart3, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';

const supplierNavItems = [
  { path: '/suppliers', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/suppliers/list', icon: Truck, label: 'Suppliers' },
  { path: '/suppliers/report', icon: BarChart3, label: 'Reports' },
  { path: '/suppliers/performance-report', icon: TrendingUp, label: 'Performance Report' },
];

const roleLabels = {
  admin: 'Admin',
  quality_manager: 'Quality Manager',
  viewer: 'Viewer',
};

export default function SupplierLayout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setChangePasswordLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setChangePasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const isPurchaseManager = isAdmin || user?.app_roles?.supplier_mgmt === 'manager';

  return (
    <div className="min-h-screen bg-white flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside 
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[240px] bg-[#F8FAFC] border-r border-slate-200 flex flex-col transition-transform duration-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="supplier-sidebar"
      >
        <div className="h-14 flex items-center px-4 border-b border-slate-200">
          <Truck className="w-6 h-6 text-emerald-600 mr-2" />
          <div className="flex-1">
            <span className="font-bold text-sm text-[#0F172A] tracking-tight block">Supplier</span>
            <span className="text-[10px] text-slate-400">Management System</span>
          </div>
          <button className="ml-auto lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-2 py-2 border-b border-slate-200">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-slate-500 hover:bg-slate-100 transition-colors"
          >
            <Home className="w-4 h-4" />
            ERP Home
            <ChevronRight className="w-3 h-3 ml-auto" />
          </button>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {supplierNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/suppliers'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-[#0F172A] text-white' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-[#0F172A]'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
          {isPurchaseManager && (
            <button
              onClick={() => navigate('/suppliers/new')}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium text-emerald-600 hover:bg-emerald-50 transition-colors mt-2"
              data-testid="nav-add-supplier"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              Add Supplier
            </button>
          )}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
                <div className="w-7 h-7 rounded-md bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-medium text-[#0F172A] truncate">{user?.name}</p>
                  <p className="text-[10px] text-slate-400">{roleLabels[user?.role]}</p>
                </div>
                <ChevronRight className="w-3 h-3 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChangePasswordModal(true)} className="text-xs" data-testid="change-password-button">
                <Key className="w-3 h-3 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-xs text-red-600" data-testid="logout-button">
                <LogOut className="w-3 h-3 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        <header className="h-14 border-b border-slate-200 flex items-center px-4 bg-white sticky top-0 z-30">
          <button className="lg:hidden mr-3 text-slate-600" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <Badge variant="outline" className="text-[10px] font-mono border-slate-300 text-slate-500">v1.0</Badge>
        </header>
        <main className="flex-1 p-4 lg:p-6">
          <PageBreadcrumb />
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" />

      <Dialog open={changePasswordModal} onOpenChange={setChangePasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Change Password</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Enter your current password and choose a new one</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-password" className="text-xs font-medium">Current Password</Label>
              <div className="relative">
                <Input id="current-password" type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="pr-10" />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-xs font-medium">New Password</Label>
              <div className="relative">
                <Input id="new-password" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" className="pr-10" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-xs font-medium">Confirm New Password</Label>
              <Input id="confirm-password" type={showNewPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setChangePasswordModal(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} className="text-xs">Cancel</Button>
            <Button onClick={handleChangePassword} disabled={changePasswordLoading || !currentPassword || !newPassword || !confirmPassword} className="text-xs bg-[#0F172A] hover:bg-[#1E293B]">
              {changePasswordLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
